import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import {
  ExternalLink,
  ExternalLinkWrapper,
  Button,
  VirtualContent,
  ModalTitle,
} from './styled';
import { Modal } from '@simplywallst/ui-core';
import { VirtualRenderer } from '@components/VirtualRenderer/VirtualRenderer';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import If from '@components/If';
import loadable from '@loadable/component';
import type { Company, FeedItem } from './types';

const Events = loadable(() => import('@/components/Events'), {
  resolveComponent: (components) => components.Events,
});
const BriefModal = loadable(() => import('@/components/BriefModal'));

export interface Props {
  index: number;
  company: Company;
  feedItem: FeedItem;
  lookUp?: string;
  isCompanyReport?: boolean;
  children: ReactNode;
}

export const FeedCardLink = ({
  children,
  index,
  company,
  feedItem,
  lookUp,
  isCompanyReport,
}: Props) => {
  const { name, tickerSymbol, canonicalUrl, companyId } = company;
  const linkTo = lookUp || canonicalUrl;
  const { url, type, subType, id, dateFormatted, title, virtual, activityId } =
    feedItem;
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'feed',
  });
  const [activeModal, setActiveModal] = useState<
    'none' | 'brief' | 'event' | 'confirm'
  >('none');
  const { isMobile, isTablet } = useUserAgent();
  const isMobileOrTablet = isMobile || isTablet;

  const trackClick = useCallback(() => {
    const eventId = id;
    trackEvent({
      action: 'click',
      modifier: 'card-item',
      index: index,
      eventType: type,
      eventSubtype: subType,
      eventId,
      activityId,
      companyId,
    });
  }, [trackEvent, index, type, subType, id, activityId, companyId]);

  const handleClick = useCallback(
    (e) => {
      if (type === 'brief') {
        setActiveModal('brief');
      } else if (
        type === 'external-article' &&
        typeof virtual !== 'undefined' &&
        virtual?.length > 0
      ) {
        setActiveModal('confirm');
        e.preventDefault();
      }
      trackClick();
    },
    [trackClick, virtual, type]
  );

  const closeModal = () => {
    setActiveModal('none');
  };

  const handleExternalLinkConfirm = useCallback(async () => {
    closeModal();
    if (RUNTIME_ENV === 'native') {
      try {
        if (typeof url === 'string') {
          const { Browser } = await import('@capacitor/browser');
          Browser.open({ url });
        }
      } catch (error) {
        console.log('Capacitor Browser error', error);
      }
    } else {
      const newTab = window.open();
      if (newTab && typeof url === 'string') {
        newTab.location.href = url;
      }
    }
    trackEvent({
      action: 'click',
      modifier: 'continue_reading_on_seeking_alpha',
      index: index,
      eventType: type,
      eventSubtype: subType,
      eventId: id,
      activityId,
    });
  }, [url]);

  return (
    <Track>
      <If
        conditions={{
          brief: type === 'brief',
          externalArticle:
            (type === 'article' || type === 'external-article') &&
            typeof url === 'string',
          event: type === 'event' || type === 'multi-event',
        }}
      >
        {{
          brief: () => (
            <Button
              onClick={() => {
                setActiveModal('brief');
                trackClick();
              }}
              data-cy-id={`brief-button-${index}`}
            >
              {children}
            </Button>
          ),
          externalArticle: () => (
            <ExternalLink
              data-cy-id={`external-article-${index}`}
              href={url}
              onClick={handleClick}
              target="_blank"
              rel={
                url.includes('https://simplywall.st')
                  ? ''
                  : 'noopener noreferrer nofollow'
              }
            >
              {children}
            </ExternalLink>
          ),
          event: () => (
            <Button
              onClick={() => {
                setActiveModal('event');
                trackClick();
              }}
              data-cy-id={`event-button-${index}`}
            >
              {children}
            </Button>
          ),
        }}
      </If>
      <If
        conditions={{
          briefModal: activeModal === 'brief',
          confirmModal: activeModal === 'confirm',
          eventModal: activeModal === 'event',
        }}
      >
        {{
          briefModal: () => (
            <BriefModal
              onClose={closeModal}
              companyId={companyId}
              activityId={activityId}
              shouldShowLink={!isCompanyReport}
            />
          ),
          confirmModal: () => (
            <Modal
              title={
                <ModalTitle>
                  <RouterLink noUnderline to={linkTo}>
                    {tickerSymbol}
                  </RouterLink>
                  {' | '}
                  {name}
                </ModalTitle>
              }
              onClose={closeModal}
              primaryLabel={
                isMobileOrTablet ? 'Close' : 'Continue reading on Seeking Alpha'
              }
              onPrimaryClick={
                isMobileOrTablet ? closeModal : handleExternalLinkConfirm
              }
              secondaryLabel={isMobileOrTablet ? undefined : 'Close'}
              onSecondaryClick={closeModal}
            >
              <VirtualContent>
                <span>{dateFormatted}</span>
                <h2>{title}</h2>
                {virtual ? (
                  <VirtualRenderer
                    nodes={virtual}
                    options={{
                      allowedNodeTypes: ['ul', 'li'],
                      allowedAttributes: [],
                    }}
                  />
                ) : null}
                {isMobileOrTablet && (
                  <ExternalLinkWrapper>
                    <ExternalLink
                      href={url as string}
                      onClick={handleExternalLinkConfirm}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                    >
                      Read full article on Seeking Alpha
                    </ExternalLink>
                  </ExternalLinkWrapper>
                )}
              </VirtualContent>
            </Modal>
          ),
          eventModal: () => <Events eventsId={id} onClose={closeModal} />,
        }}
      </If>
    </Track>
  );
};
